import React from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  History,
  RefreshCw,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { getCreditLedgerPaginated } from "@/services/creditService";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { CreditLedgerEntry } from "@/types/credits";
import { cn } from "@/lib/utils";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { useNavigate } from "react-router-dom";

const labelMap: Record<string, string> = {
  STANDARD_REPORT: "Standard Report",
  ECO_PREDICT: "EcoPredict Prediction",
};

function timeAgo(iso?: string) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function UsageDetails() {
  const { user, loading: authLoading } = useSupabaseAuth();
  const userId = user?.id;
  const navigate = useNavigate();

  // The dashboard injects user context elsewhere; use hook to read session
  const { balance, loading, regular, bonus, refresh } =
    useCreditBalance(userId);
  const [history, setHistory] = React.useState<CreditLedgerEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalItems, setTotalItems] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);

  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const fetchHistory = React.useCallback(
    async (page: number) => {
      if (!userId) return;

      setLoadingHistory(true);
      const offset = (page - 1) * itemsPerPage;

      try {
        const result = await getCreditLedgerPaginated(
          userId,
          itemsPerPage,
          offset,
        );
        setHistory(result.entries);
        setTotalItems(result.total);
        setHasMore(result.hasMore);
      } catch (err) {
        console.error("Error fetching usage history", err);
      } finally {
        setLoadingHistory(false);
      }
    },
    [userId, itemsPerPage],
  );

  React.useEffect(() => {
    fetchHistory(currentPage);
  }, [fetchHistory, currentPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (authLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-xl font-semibold">Sign in to see your usage</h2>
        <p className="text-sm text-muted-foreground mt-2">
          Please sign in to view your credit balances and recent activity.
        </p>
        <div className="mt-4">
          <Link to="/profile">
            <Button>Sign in / Profile</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-[50%] mx-auto p-6">
      <button
        onClick={() => navigate(-1)}
        className="mb-[5rem] mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground bg-black/5 p-2 rounded-md"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
      {/* Header Section */}
      <div>
        <div className="mx-auto gap-8">
          <div className="">
            <h2 className="text-2xl font-semibold">Credit Usage</h2>
            <p className="text-sm text-muted-foreground">
              Track your balance and recent credit usage
            </p>

            {/* Action buttons */}
            <div className="mt-3 flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={refresh}>
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>

              <Link to="/purchase-credits">
                <Button className="font-semibold" size="sm">
                  Buy credits
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Separator className="my-6" />
      {/* Balance Overview */}
      <div className="mt-6 rounded-xl border bg-white dark:bg-gray-900 p-6">
        <div className="flex items-center justify-between">
          <div className="flex gap-12">
            <div>
              <p className="text-[2rem] text-muted-foreground font-semibold">
                Credits Used
              </p>
              <div className="text-[3rem] font-semibold text-orange-500 mt-1">
                {loadingHistory
                  ? "…"
                  : Math.abs(
                      history.reduce(
                        (sum, entry) =>
                          sum +
                          (entry.creditsDelta < 0 ? entry.creditsDelta : 0),
                        0,
                      ),
                    )}{" "}
                <span className="text-sm ">TOTAL</span>
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-blue-50 dark:bg-green-900/20 p-4">
            <div>
              <p className="text-sm text-muted-foreground font-semibold">
                Credits available
              </p>
              <div className="text-4xl font-semibold text-blue-500 mt-1">
                {loading ? "…" : balance}
              </div>
            </div>

            <div className="mt-4 text-sm text-muted-foreground">
              <div className="flex justify-between gap-8 font-semibold">
                <span>Base</span>
                <span className="font-medium text-foreground">{regular}</span>
              </div>
              <div className="flex justify-between gap-8 mt-1 font-semibold">
                <span>Bonus</span>
                <span className="font-medium text-foreground">{bonus}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            <h4 className="font-medium">Recent activity</h4>
          </div>
        </div>

        {loadingHistory ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent activity.</p>
        ) : (
          <>
            <ul className="divide-y rounded-lg border bg-white dark:bg-gray-900">
              {history.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div>
                    <div className="text-sm font-medium">
                      {labelMap[entry.reason] ||
                        entry.reason ||
                        entry.description}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {timeAgo(entry.createdAt)}
                    </div>
                  </div>

                  <div className="text-right">
                    <div
                      className={cn(
                        "text-sm font-medium",
                        entry.creditsDelta < 0
                          ? "text-foreground"
                          : "text-green-500",
                      )}
                    >
                      {entry.creditsDelta < 0 ? "−" : "+"}
                      {Math.abs(entry.creditsDelta)}
                    </div>

                    {entry.credits_type === "bonus" && (
                      <div className="text-[10px] text-muted-foreground">
                        bonus credits
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                  {totalItems} transactions
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          variant={
                            currentPage === pageNum ? "default" : "outline"
                          }
                          size="sm"
                          className="w-8 h-8 p-0"
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
